'use client';
import React, { useMemo, useRef } from 'react';
import { DoubleRangeSliderProps } from './types';
import styles from './DoubleRangeSlider.module.css';

const DoubleRangeSlider = ({ label, max, min, onChange, step, value }: DoubleRangeSliderProps) => {
  const [inputFrom, inputTo] = value || [0, 0];
  const sliderRef = useRef<null | HTMLElement>(null);

  const adjustedMax = useMemo(() => {
    const stepsFromMin = Math.floor((max - min) / step);
    return min + stepsFromMin * step;
  }, [min, max, step]);

  const sliderStyle = useMemo(() => {
    if (inputFrom >= inputTo) {
      return {
        left: `${((inputTo - min) / (adjustedMax - min)) * 100}%`,
        right: `${100 - ((inputFrom - min) / (adjustedMax - min)) * 100}%`,
      };
    }
    return {
      left: `${((inputFrom - min) / (adjustedMax - min)) * 100}%`,
      right: `${100 - ((inputTo - min) / (adjustedMax - min)) * 100}%`,
    };
  }, [inputFrom, inputTo, adjustedMax, min]);

  const handleFromInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    if (val < inputTo) {
      onChange?.(val, inputTo);
    } else {
      e.target.value = inputFrom.toString();
    }
  };

  const handleToInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    if (val > inputFrom) {
      onChange?.(inputFrom, val);
    } else {
      e.target.value = inputTo.toString();
    }
  };

  const dots = useMemo(() => {
    const result: number[] = [];
    for (let i = min; i <= adjustedMax; i += step) {
      result.push(i);
    }
    return result;
  }, [min, adjustedMax, step]);

  return (
    <div className={styles.sliderContainer}>
      <div className={styles.sliderLabel}>{label}</div>
      <div className={styles.currentValues}>
        <span className={styles.currentValue}>{inputFrom?.toFixed(1)}</span>
        <span className={styles.currentValue}>{inputTo?.toFixed(1)}</span>
      </div>
      <div className="flex justify-center items-center gap-2">
        <span className={styles.rangeLabel}>{min?.toFixed(1)}</span>
        <div className="slider-wrapper flex-1">
          <div className={styles.rangeSlider}>
            <span className={styles.rangeSelected} ref={sliderRef} style={sliderStyle}></span>
            {dots.map((dot, index) => (
              <span
                className={styles.dot}
                key={index}
                style={{ left: `${((dot - min) / (adjustedMax - min)) * 99}%` }}
              ></span>
            ))}
          </div>
          <div className={styles.rangeInput}>
            <input
              max={adjustedMax}
              min={min}
              onChange={handleFromInput}
              step={step}
              title="min"
              type="range"
              value={inputFrom}
            />
            <input
              max={adjustedMax}
              min={min}
              onChange={handleToInput}
              step={step}
              title="max"
              type="range"
              value={inputTo}
            />
          </div>
        </div>
        <span className={styles.rangeLabel}>{adjustedMax?.toFixed(1)}</span>
      </div>
    </div>
  );
};

export default DoubleRangeSlider;
